import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useImmerReducer } from "use-immer";
import { default as Fi, defaultStyles } from "react-file-icon";
import { useDropzone } from "react-dropzone";
import ActiveArea from "components/Shared/MDropzone/ActiveArea";
import { default as Actions } from "components/Shared/MDropzone/FileActions";
import { default as OverwriteDialog } from "components/Shared/MDropzone/FileOverwriteDialog";
import { MDropzoneContext } from "components/Shared/MDropzone/contexts";
import { MTable } from "components/Shared";
import { dateTimeToString } from "components/Shared/Formatters";

import {
  actionTypes,
  customValidation,
  parseAcceptedFiles,
  fileTypes,
  methodDefaults,
  useDropzoneProperties,
  useExistingFiles,
  useParseValidFileTypes,
} from "components/Shared/MDropzone/utility";

const initialState = {
  files: [],
  errors: [],
  filesThatWillOverwrite: [],
  showDialog: false,
  trackFiles: false,
};

const reducer = ({
  onFileAdd,
  onFileRemove,
}) => (
  
  draft,
  { type, payload }
) => {
    switch (type) {
      case actionTypes.SET_FILES:
        onFileRemove(draft.files);
        onFileAdd(payload);
        draft.files = payload;
        return;
      case actionTypes.ADD_FILES: {
        draft.filesThatWillOverwrite = payload.filter(({ name }) => draft.files.find(file => file.name === name)) || [];
        if (draft.filesThatWillOverwrite.length !== 0)
          draft.showDialog = true;
        const filesThatWillNotOverwrite = payload.filter(({ name }) => !draft.filesThatWillOverwrite.find(file => file.name == name));
        onFileAdd(filesThatWillNotOverwrite);
        if (draft.trackFiles) draft.files = draft.files.concat(filesThatWillNotOverwrite);
        return;
      }
      case actionTypes.REMOVE_FILE:
        onFileRemove([payload]);
        draft.files = draft.files.filter(({ name }) => name !== payload.name);
        return;
      case actionTypes.CLEAR:
        onFileRemove(draft.files);
        draft.files = initialState.files;
        draft.errors = initialState.errors;
        return;
      case actionTypes.SET_ERRORS:
        if (payload && payload.length !== 0) {
          draft.errors = payload;
          draft.files = draft.files.filter(({ name }) => name !== payload.file.name);
        }
        else {
          draft.errors = initialState.errors;
        }
        return;
      case actionTypes.ADD_ERRORS:
        if (payload && payload.length !== 0) {
          draft.errors = draft.errors.filter(({ file: { name } }) => name !== payload.file.name).concat(payload);
          draft.files = draft.files.filter(({ name }) => !payload.find(({ file }) => file.name === name));
        }
        return;
      case actionTypes.CLOSE_DIALOG:
        draft.filesThatWillOverwrite = [];
        draft.showDialog = false;
        return;
      case actionTypes.OVERWRITE_FILES:
        onFileAdd(payload);
        draft.files = draft.files.filter(file => !payload.find(({ name }) => name === file.name)).concat(payload);
        draft.filesThatWillOverwrite = [];
        draft.showDialog = false;
        return;
      case actionTypes.OVERWRITE_FILES_NO_CALLBACK:
        draft.files = payload;
        draft.filesThatWillOverwrite = [];
        draft.showDialog = false;
        return;
      case actionTypes.SET_TRACK_FILES:
        draft.trackFiles = payload;
        return;
    }
  };

const MDropzone = ({
  setDropzoneProperties,
  validFileTypes,
  multiple,
  onPreview,
  onFileAdd,
  onFileRemove,
  existingFiles,
  prompt,
  fileRightOverrides,
  trackFiles,
  columnDefinitionOverride,
  ...otherProps
}) => {
  const {
    accept
  } = useParseValidFileTypes(validFileTypes);
  const [{
    files,
    errors,
    filesThatWillOverwrite,
    showDialog,
  }, localDispatch] = useImmerReducer(reducer({ onFileAdd, onFileRemove }), initialState);
  const addFiles = payload => localDispatch({ type: actionTypes.ADD_FILES, payload });
  const overwriteFiles = payload => localDispatch({ type: actionTypes.OVERWRITE_FILES, payload });
  const overwriteFilesNoCallback = payload => localDispatch({ type: actionTypes.OVERWRITE_FILES_NO_CALLBACK, payload });
  const removeFile = payload => localDispatch({ type: actionTypes.REMOVE_FILE, payload });
  useExistingFiles({ overwriteFilesNoCallback, existingFiles, });
  const setFiles = payload => localDispatch({ type: actionTypes.SET_FILES, payload });
  const addErrors = payload => localDispatch({ type: actionTypes.ADD_ERRORS, payload });
  const setErrors = payload => localDispatch({ type: actionTypes.SET_ERRORS, payload });
  const clear = useCallback(() => localDispatch({ type: actionTypes.CLEAR }), []);
  const closeDialog = () => localDispatch({ type: actionTypes.CLOSE_DIALOG });
  useDropzoneProperties({ setDropzoneProperties, properties: { clear, files, errors } });

  useEffect(() => {
    localDispatch({ type: actionTypes.SET_TRACK_FILES, payload: trackFiles });
  }, [trackFiles]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept
  } = useDropzone({
    ...otherProps,
    accept,
    multiple,
    onDropAccepted: async acceptedFiles => {
      const { validatedAcceptedFiles, errors } = await customValidation({
        parsedAcceptedFiles: parseAcceptedFiles(acceptedFiles),
        validFileTypes,
      });
      if (multiple) {
        addFiles(validatedAcceptedFiles);
        addErrors(errors);
      } else {
        setFiles(validatedAcceptedFiles);
        setErrors(errors);
      }
    },
  });

  const mapFilesToTable = () => files.map(f => ({
    ...f,
    name: <>
      <Fi
        extension={f.extension}
        {...defaultStyles[f.extension]}
        size={49}
        fold={false}
      />
      <p>{f.name}</p>
    </>,
    lastModifiedDate: f.lastModifiedDate,
    extension: f.extension,
    size: f.size,
    actions: <Actions file={f} rightOverrides={fileRightOverrides} />
  }));

  return (
    <MDropzoneContext.Provider value={{
      getRootProps,
      getInputProps,
      isDragAccept,
      isDragActive,
      onPreview,
      removeFile,
      showDialog,
      closeDialog,
      overwriteFiles,
      filesThatWillOverwrite,
    }}><>
        <ActiveArea>
          <MTable
            className="file-list-table"
            columnDefinitions={columnDefinitionOverride ? columnDefinitionOverride : [
              { name: "Name", dataSource: "name", className: "file-name-cell" },
              { name: "Date Modified", dataSource: data => dateTimeToString(data.lastModifiedDate), style: { width: "150px" } },
              { name: "Type", dataSource: "extension", style: { width: "50px" } },
              { name: "Size", dataSource: "size", style: { width: "80px" } },
              { name: "Actions", dataSource: "actions", style: { width: "160px" } },
            ]}
            data={mapFilesToTable()}
            emptyTableText={prompt ? prompt : "No files present"}
          />
        </ActiveArea>
        <OverwriteDialog />
      </></MDropzoneContext.Provider>
  );
};

MDropzone.defaultProps = {
  prompt: null,
  // Will accept any file type by default
  validFileTypes: fileTypes.ANY,
  rejectMessage: null,
  multiple: false,
  onPreview: () => { },
  setDropzoneProperties: () => { },
  onDropAccepted: () => { },
  onFileRemove: () => { },
  onClear: () => { },
  existingFiles: [],
  columnDefinitionOverride: null
};

MDropzone.propTypes = {
  setDropzoneProperties: PropTypes.func,
  // OPTIONAL
  // Definitions of which file types this Dropzone can accept
  // For Type Documentation https://github.com/react-dropzone/attr-accept
  validFileTypes: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string,
    ])),
    PropTypes.object,
    PropTypes.string,
  ]),
  // Custom string to display when the file is rejected
  rejectMessage: PropTypes.string,
  // Allow multiple files to be selected
  multiple: PropTypes.bool,
  // Callback for when a file is previewed
  onPreview: PropTypes.func,
  // Callback for when a file passes through validation will pass an array of files even if one file to callback
  onFileAdd: PropTypes.func,
  // Callback for when a file is removed will pass an array of files even if one file in to callback
  onFileRemove: PropTypes.func,
  // Callback for when the dropzone is cleared of all files
  onClear: PropTypes.func,
  // Files that should be in the dropzone when it loads
  // {fileName, lastModified, downloadUrl, viewUrl, fileSizeBytes}
  existingFiles: PropTypes.array,
  // Message to display when there are no files
  prompt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  // Define overrides for file action rights: download, preview, delete.
  // NOTE: Only use if the component is not displaying files for a case and you are sure case file rights are not relevant.
  /*
  {
    download: false, // Disable download
    preview: false, // Disble preview
    delete: true, // Enable delete
  }
  */
  fileRightOverrides: PropTypes.object,
  // Add to files on drag and drop, don't wait for upload
  // Use this is you want MDropzone to handle the files itself rather than waiting for uploads to complete, then passing back new existing files
  trackFiles: PropTypes.bool,

  columnDefinitionOverride: PropTypes.array
};

// Reference when implementing for validDileTypes prop
MDropzone.fileTypes = fileTypes;
// If you going to make use of the setMethods prop then reference this as the initialstate if you'd like to destructure the methods object when assigning
// Example in Customer/Order/Batch
MDropzone.methodDefaults = methodDefaults;


export default MDropzone;